import { memo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import dynamic from 'next/dynamic';
import styled from 'styled-components';
import {
  selectListScene,
  selectSceneSelected,
  selectTitleProject,
} from '../selectors';
import { I_Scene } from '../types';
import { HeritagePlaqueHost } from '../../../features/HeritagePlaque';
import PlaqueHitOverlay from '../../../features/HeritagePlaque/PlaqueHitOverlay';
import IncenseHitOverlay from '../../../features/SpiritualServices/IncenseHitOverlay';

const Tour = dynamic(() => import('@containers/Home/components/Tour'), {
  ssr: false,
});
const FloatingChatbot = dynamic(
  () => import('@components/ChatbotWidget/FloatingChatbot'),
  { ssr: false, loading: () => null },
);

interface Props {
  onChangeScene: (scene: I_Scene) => void;
  onNextScene: () => void;
}

const DemoRoot = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: #120c0a;

  .copyright,
  #themeControlbar,
  .krpano-logo,
  #krpano_logo,
  a[href*='krpano.com'] {
    display: none !important;
  }
`;

const DemoHud = styled.div`
  position: absolute;
  top: 16px;
  right: 0;
  left: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 0 160px;
  pointer-events: none;
  color: #fff8ea;

  > * {
    pointer-events: auto;
  }

  @media (max-width: 720px) {
    top: 10px;
    padding: 0 12px;
  }
`;

const HudCluster = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: min(380px, 100%);
  padding: 8px 10px;
  border: 1px solid rgba(231, 189, 98, 0.45);
  border-radius: 999px;
  background: #18100e;
`;

const HudCopy = styled.div`
  min-width: 0;
  text-align: center;

  strong {
    display: block;
    overflow: hidden;
    color: #f3d48a;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-overflow: ellipsis;
    text-transform: uppercase;
    white-space: nowrap;
  }

  span {
    display: block;
    overflow: hidden;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.3;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const HudButton = styled.button`
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  border: 1px solid rgba(231, 189, 98, 0.45);
  border-radius: 50%;
  background: rgba(255, 248, 234, 0.08);
  color: #fff8ea;
  font: inherit;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.35;
  }

  &:hover:not(:disabled) {
    background: rgba(231, 189, 98, 0.2);
  }
`;

const ThemeDemo = ({ onChangeScene, onNextScene }: Props) => {
  const titleProject = useSelector(selectTitleProject);
  const sceneSelected = useSelector(selectSceneSelected);
  const listScene = useSelector(selectListScene);
  const [tourReady, setTourReady] = useState(false);

  useEffect(() => {
    setTourReady(true);
  }, []);

  const index = listScene.findIndex(scene => scene.id === sceneSelected?.id);
  const previousScene = index > 0 ? listScene[index - 1] : undefined;
  const followingScene =
    index >= 0 && index < listScene.length - 1 ? listScene[index + 1] : undefined;

  return (
    <DemoRoot>
      {tourReady ? (
        <Tour
          hideAuxiliaryUi
          onChangeScene={onChangeScene}
          onNextScene={onNextScene}
        />
      ) : null}
      <DemoHud>
        <HudCluster>
          <HudButton
            type="button"
            aria-label="Điểm xem trước"
            disabled={!previousScene}
            onClick={() => previousScene && onChangeScene(previousScene)}
          >
            ‹
          </HudButton>
          <HudCopy>
            <strong>{titleProject || 'Đền Bạch Mã'}</strong>
            <span>{sceneSelected?.title || 'Đang tải điểm xem'}</span>
          </HudCopy>
          <HudButton
            type="button"
            aria-label="Điểm xem tiếp"
            disabled={!followingScene}
            onClick={() => followingScene && onChangeScene(followingScene)}
          >
            ›
          </HudButton>
        </HudCluster>
      </DemoHud>
      <FloatingChatbot />
      <PlaqueHitOverlay />
      <IncenseHitOverlay />
      <HeritagePlaqueHost />
    </DemoRoot>
  );
};

export default memo(ThemeDemo);
